import { Icon } from '@/components';
import { cn } from '@/lib/cn';
import { A } from '@solidjs/router';
import type { Machine, MachineSample } from '@uchat-team-management/shared';
import { For, Show } from 'solid-js';
import { Ago, MachineStatusBadge, MockBadge, StaleNotice } from './StatusBadge';
import { bytes, pct, perSecond } from './format';

const Meter = (props: { used: number; total: number }) => {
  const ratio = () => (props.total > 0 ? Math.min(1, props.used / props.total) : 0);
  return (
    <div class="h-1.5 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
      <div
        class={cn(
          'h-full rounded-full transition-[width] duration-200 ease-out motion-reduce:transition-none',
          ratio() > 0.9 ? 'bg-red-500' : ratio() > 0.75 ? 'bg-amber-500' : 'bg-brand-600',
        )}
        style={`width:${ratio() * 100}%`}
      />
    </div>
  );
};

const Stat = (props: { label: string; value: string; sub?: string }) => (
  <div class="min-w-0">
    <div class="text-xs text-neutral-500">{props.label}</div>
    <div class="truncate text-sm font-semibold tabular-nums">{props.value}</div>
    <Show when={props.sub}>
      <div class="truncate text-xs text-neutral-500 tabular-nums">{props.sub}</div>
    </Show>
  </div>
);

/** Current machine-level readings (shared by every runner on the machine). */
export const MachineStats = (props: { sample: MachineSample }) => (
  <div class="space-y-3">
    <div class="grid grid-cols-2 gap-3 sm:grid-cols-5">
      <Stat label="CPU" value={pct(props.sample.cpuPct)} />
      <Stat
        label="RAM"
        value={`${bytes(props.sample.mem.usedBytes)} / ${bytes(props.sample.mem.totalBytes)}`}
      />
      <Stat
        label="Swap"
        value={`${bytes(props.sample.swap.usedBytes)} / ${bytes(props.sample.swap.totalBytes)}`}
      />
      <Stat
        label="Network"
        value={`↓ ${perSecond(props.sample.net.rxBps)}`}
        sub={`↑ ${perSecond(props.sample.net.txBps)}`}
      />
      <Stat
        label="Disk I/O"
        value={`R ${perSecond(props.sample.diskIo.readBps)}`}
        sub={`W ${perSecond(props.sample.diskIo.writeBps)}`}
      />
    </div>
    <div class="space-y-2">
      <For each={props.sample.disks}>
        {(d) => (
          <div>
            <div class="flex justify-between gap-2 text-xs">
              <span class="truncate font-medium">{d.mount}</span>
              <span class="shrink-0 tabular-nums text-neutral-500">
                {bytes(d.usedBytes)} used · {bytes(d.totalBytes - d.usedBytes)} free
              </span>
            </div>
            <Meter used={d.usedBytes} total={d.totalBytes} />
          </div>
        )}
      </For>
    </div>
  </div>
);

export const MachineCard = (props: { machine: Machine; runnerCount: number }) => (
  <A
    href={`/runners/machines/${props.machine.id}`}
    class="block rounded-xl border border-neutral-200 bg-surface p-4 shadow-sm transition-shadow duration-200 ease-out hover:shadow-md motion-reduce:transition-none dark:border-neutral-800 dark:bg-surface-dark"
  >
    <div class="mb-3 flex items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <Icon name={props.machine.os === 'macos' ? 'apple' : 'computer'} class="h-4 w-4" />
          <span class="truncate font-semibold">{props.machine.name}</span>
          <MockBadge source={props.machine.source} />
        </div>
        <div class="mt-0.5 text-xs text-neutral-500">
          {props.machine.os === 'macos' ? 'macOS' : 'Windows'}
          {props.machine.arch ? ` · ${props.machine.arch}` : ''} · {props.runnerCount} runner
          {props.runnerCount === 1 ? '' : 's'} · last seen <Ago iso={props.machine.lastSeenAt} />
        </div>
      </div>
      <MachineStatusBadge online={props.machine.online} />
    </div>
    <Show when={!props.machine.online}>
      <div class="mb-3">
        <StaleNotice lastSeenAt={props.machine.lastSeenAt} />
      </div>
    </Show>
    <Show
      when={props.machine.latest}
      fallback={
        <p class="text-sm text-neutral-500">No data yet — install the agent (see Add machine).</p>
      }
    >
      {(sample) => (
        <div classList={{ 'opacity-60': !props.machine.online }}>
          <MachineStats sample={sample()} />
        </div>
      )}
    </Show>
  </A>
);
